import type { CreateDataFlowScheduleRequest, ScheduleDetails } from '@/features/adp/services/pipeline/pipeline.types'
import type { UpdateRecordParams } from '@/features/adp/services/common/common.types'
import { padNumber, type SchedulePipelineFormValues } from '../schemas/SchedulePipeline'

// Returns schedule type id based on form values
export function getScheduleTypeId(values: SchedulePipelineFormValues): number {
    if (values.mode === 'onetime') return 1
    if (values.frequency === 'hourly') return 3
    if (values.frequency === 'daily') return 4
    if (values.frequency === 'weekly') return 5
    return 2
}

// Converts form values into ScheduleDetails (store shape)
export function formValuesToScheduleDetails(values: SchedulePipelineFormValues): ScheduleDetails {
    const d = values.startDate
    const t = values.startTime

    if (!d || !t) {
        throw new Error('Schedule form is missing date or time.')
    }

    const startDate = new Date(d)
    const [hourStr, minuteStr] = t.split(':')
    const frequencyValue = values.frequencyhrs ?? 0

    return {
        start_day: startDate.getDate(),
        start_month: startDate.getMonth() + 1,
        start_year: startDate.getFullYear(),
        start_time_hour: Number(hourStr),
        start_time_minutes: Number(minuteStr),
        frequency_hours: values.frequency === 'hourly' ? frequencyValue : 0,
        frequency_minutes: values.frequency === 'minute-wise' ? frequencyValue : 0,
        frequency_day_of_week: startDate.getDay(),
        schedule_type_id: getScheduleTypeId(values),
    }
}

// Builds update payload for existing schedule
export function buildScheduleUpdatePayload(
    dataFlowId: number,
    values: SchedulePipelineFormValues,
): UpdateRecordParams {
    const d = values.startDate
    const t = values.startTime

    if (!d || !t) {
        throw new Error('Schedule form is missing date or time.')
    }

    const startDate = new Date(d)
    const [hourStr, minuteStr] = t.split(':')

    const startHour = Number(hourStr)
    const startMin = Number(minuteStr)

    const scheduleTypeId = getScheduleTypeId(values)
    const frequencyValue = values.frequencyhrs ?? 0

    const frequency_hours = values.frequency === 'hourly' ? frequencyValue : 0
    const frequency_minutes = values.frequency === 'minute-wise' ? frequencyValue : 0

    return {
        set_keys: [
            'start_day',
            'start_month',
            'start_year',
            'start_time_hour',
            'start_time_minutes',
            'frequency_hours',
            'frequency_minutes',
            'frequency_day_of_week',
            'schedule_type_id',
        ],
        set_values: [
            String(startDate.getDate()),
            String(startDate.getMonth() + 1),
            String(startDate.getFullYear()),
            String(startHour),
            String(startMin),
            String(frequency_hours),
            String(frequency_minutes),
            String(startDate.getDay()),
            String(scheduleTypeId),
        ],
        where_keys: ['data_flow_id'],
        where_values: [String(dataFlowId)],
    }
}

// Builds create payload for new schedule
export function buildScheduleCreatePayload(
    pipelineName: string,
    values: SchedulePipelineFormValues,
): CreateDataFlowScheduleRequest {
    const startDate = new Date(values.startDate)
    const [hourStr, minuteStr] = values.startTime.split(':')

    const startHour = Number(hourStr)
    const startMin = Number(minuteStr)

    const scheduleTypeId = getScheduleTypeId(values)
    const frequencyValue = values.frequencyhrs ?? 0

    return {
        job_schedular: [
            {
                data_flow_name: pipelineName,
                start_day: startDate.getDate(),
                start_month: startDate.getMonth() + 1,
                start_year: startDate.getFullYear(),
                start_time_hour: startHour,
                start_time_minutes: startMin,
                frequency_hours: values.frequency === 'hourly' ? frequencyValue : 0,
                frequency_minutes: values.frequency === 'minute-wise' ? frequencyValue : 0,
                frequency_day_of_week: startDate.getDay(),
                schedule_type_id: scheduleTypeId,
            },
        ],
    }
}

// Determines if schedule is one-time
function isOneTimeSchedule(typeId: number, normalizedType: string): boolean {
    return (
        typeId === 1 ||
        normalizedType.includes('one_time') ||
        normalizedType.includes('onetime')
    )
}

// Resolves frequency and frequencyhrs for repeated schedules
function resolveFrequency(
    typeId: number,
    normalizedType: string,
    frequencyHours: number,
    frequencyMinutes: number,
): {
    frequency: NonNullable<SchedulePipelineFormValues['frequency']>
    frequencyhrs: number
} {
    if (frequencyMinutes > 0 || typeId === 2) {
        return {
            frequency: 'minute-wise',
            frequencyhrs: frequencyMinutes || 5,
        }
    }

    if (frequencyHours > 0 || typeId === 3) {
        return {
            frequency: 'hourly',
            frequencyhrs: frequencyHours || 5,
        }
    }

    if (typeId === 5 || normalizedType.includes('weekly')) {
        return {
            frequency: 'weekly',
            frequencyhrs: 5,
        }
    }

    return {
        frequency: 'daily',
        frequencyhrs: 5,
    }
}

// Builds initial form values from schedule (edit) or defaults (create)
export function buildFormValuesFromSchedule(
    isSchedule?: boolean,
    scheduleDetails?: ScheduleDetails,
    scheduleTypeName?: string | null,
): SchedulePipelineFormValues {

    if (!scheduleDetails) {
        return {
            mode: isSchedule ? 'repeated' : 'onetime',
            frequency: 'daily',
            frequencyhrs: 5,
            startDate: '',
            startTime: '',
        }
    }

    const frequencyHours = Number(scheduleDetails.frequency_hours ?? 0)
    const frequencyMinutes = Number(scheduleDetails.frequency_minutes ?? 0)
    const typeId = scheduleDetails.schedule_type_id
    const normalizedType = scheduleTypeName?.toLowerCase() ?? ''

    const isOnetime = isOneTimeSchedule(typeId, normalizedType)

    const defaultFrequency: {
        frequency: NonNullable<SchedulePipelineFormValues['frequency']>
        frequencyhrs: number
    } = {
        frequency: 'daily',
        frequencyhrs: 5,
    }

    const { frequency, frequencyhrs } = isOnetime
        ? defaultFrequency
        : resolveFrequency(typeId, normalizedType, frequencyHours, frequencyMinutes)

    return {
        mode: isOnetime ? 'onetime' : 'repeated',
        frequency,
        frequencyhrs,
        startDate: `${scheduleDetails.start_year}-${padNumber(scheduleDetails.start_month)}-${padNumber(scheduleDetails.start_day)}`,
        startTime: `${padNumber(scheduleDetails.start_time_hour)}:${padNumber(scheduleDetails.start_time_minutes)}`,
    }
}